import { Placement } from '@noths/polaris-client-ribbons-design-system';
import {
  getProductIdFromProductCode,
  handleFetchResponse,
  richRelevancePlacementSingleton,
} from '@noths/polaris-client-utils';
import type {
  RichRelevancePlacement,
  RichRelevancePlacementItem,
  SimilarProduct,
} from '@noths/polaris-dev-ts-types';
import { createAsyncThunk } from '@reduxjs/toolkit';
import fetch from 'isomorphic-fetch';
import getConfig from 'next/config';

import { ReduxApplicationState } from 'src/redux/combinedReducer';
import { NextConfig, ProductPlacementRecord, ProductPlacementRecordError } from 'src/types';
import { logger } from 'src/utils';

const { endpoints } = (getConfig() as NextConfig).publicRuntimeConfig;

export type ProductFromResponse = Omit<SimilarProduct, 'linkURL'>;

const decorateProductsWithRRData = (
  products: ProductFromResponse[],
  placementItems: RichRelevancePlacementItem[],
): SimilarProduct[] =>
  products.map((product) => {
    const placementItem = placementItems.find(
      ({ id }) => getProductIdFromProductCode(product.code) === Number(id),
    );

    return {
      ...product,
      linkURL: placementItem?.linkURL ?? '',
    };
  });

export const requestSimilarProducts = createAsyncThunk<
  ProductPlacementRecord | null,
  RichRelevancePlacement,
  {
    rejectValue: ProductPlacementRecordError;
  }
>(
  'richRelevance/requestSimilarProducts',
  async ({ placement_name, items = [], message, strategy }, thunkApi) => {
    const ids = items.map((p) => p.id).join('&ids[]=');

    try {
      const apiResponse = await fetch(`${endpoints.recommendedProducts}?ids[]=${ids}`);

      const { products } = ((await handleFetchResponse(apiResponse)) || {}) as Record<
        'products',
        ProductFromResponse[]
      >;

      const similarProducts = decorateProductsWithRRData(products, items);

      return {
        products: similarProducts,
        placementType: placement_name as Placement,
        placementTitle: message,
        placementStrategy: strategy,
      };
    } catch (err) {
      const error = err as Error;

      logger.error({
        location: 'requestSimilarProducts',
        extra: { description: `Exception thrown calling handleFetchResponse.`, error },
      });

      return thunkApi.rejectWithValue({
        error: { message: error.message, stack: error.stack, name: error.name },
        placementType: placement_name,
      } as ProductPlacementRecordError);
    }
  },
);

export const requestCrossSellProducts = createAsyncThunk(
  'richRelevance/requestCrossSellProducts',
  async (_, { getState, dispatch }) => {
    const {
      product: { code, title },
    } = getState() as ReduxApplicationState;

    const rrManager = richRelevancePlacementSingleton.getInstance();

    rrManager.addPlacementRequestToQueue({
      placements: [Placement.CrossSell],
      itemId: String(getProductIdFromProductCode(code)),
      itemName: title,
      renderDynamicPlacements: null,
    });

    await rrManager.runRequestsInQueue();

    const placement = rrManager
      .getPlacements()
      .find((pl) => pl.placement_name === Placement.CrossSell);

    if (!placement) {
      return null;
    }

    const placementRecord = await dispatch(requestSimilarProducts(placement)).unwrap();

    return placementRecord;
  },
);
